import { createServerFn } from '@tanstack/react-start';
import { languages } from '@worldcoo/monolith/core';
import { currencyCodes } from '@worldcoo/monolith/iso/4217';
import {
	getNgoSettings,
	putNgoSettings,
	getNgoPortals,
	getNgoPortal,
	updateNgoPortal,
	getNgoProject,
	createNgoProject,
	deleteNgoProject,
	putNgoProfileById,
	getNgoById,
} from '@worldcoo/monolith/apis/portals';
import {
	getDraftNgos,
	putDraftNgos,
	getCdnUrl,
	getSignedUrlForOneHour,
} from '@worldcoo/monolith/apis/backoffice/server';
import { cache, refetch } from './server-cache.server';
import { ngoProjectsCacheOptions, projectCollectedCacheOptions } from './server-cache-options.server';
import * as z from 'zod';
import {
	isAdminSession,
	requireSession,
	UNAUTHENTICATED,
	projectSchema,
	translatableText,
	filterProjects,
	projectFilterSchema,
} from './server-functions-shared.server';

function resolveCdnFileUrl(value: string | undefined): string | undefined {
	if (!value) return undefined;
	if (value.startsWith('http')) return value;
	return getCdnUrl(value);
}

async function resolveFileUrl(value: string | undefined): Promise<string | undefined> {
	if (!value) return undefined;
	if (value.startsWith('http')) return value;
	try {
		return await getSignedUrlForOneHour(value);
	} catch {
		return undefined;
	}
}

export type PutProjectErrorCode = 'PROJECT_TOO_LARGE' | 'SAVE_PROJECT_FAILED';

export const getProjectCollectedFn = createServerFn()
	.inputValidator(z.object({ projectId: z.string() }))
	.handler(({ data: { projectId } }) => {
		return cache(projectCollectedCacheOptions(projectId));
	});

export const getNgoProjectsFn = createServerFn()
	.inputValidator(z.object({ ...projectFilterSchema.shape }))
	.handler(async ({ data: filters }) => {
		const session = requireSession();
		if ('unauthenticated' in session) return UNAUTHENTICATED;
		const { ngoId } = session;
		const result = await cache(ngoProjectsCacheOptions(ngoId));
		return { projects: filterProjects(result.projects, filters) };
	});

export const getNgoProjectFn = createServerFn()
	.inputValidator(z.object({ projectId: z.string() }))
	.handler(async ({ data: { projectId } }) => {
		const session = requireSession();
		if ('unauthenticated' in session) return UNAUTHENTICATED;
		const { ngoId } = session;
		const project = await getNgoProject(projectId);
		if ('notFound' in project) return project;
		// Verify the project belongs to the authenticated NGO (admins can access any)
		if (!isAdminSession() && project.ngoId !== ngoId) {
			return { notFound: `Project ${projectId} not found` };
		}

		// Resolve image/video S3 keys to CDN URLs
		const coverImageUrl = resolveCdnFileUrl(project.coverImage);
		const coverVideoUrl = resolveCdnFileUrl(project.coverVideo);
		const otherImageUrls = (project.otherImages ?? []).map((key) => resolveCdnFileUrl(key));

		return {
			...project,
			coverImageUrl,
			coverVideoUrl,
			otherImageUrls: otherImageUrls.filter((url): url is string => Boolean(url)),
		};
	});

export const putNgoProjectFn = createServerFn({ method: 'POST' })
	.inputValidator(z.object({ project: projectSchema }))
	.handler(async ({ data: { project } }) => {
		const session = requireSession();
		if ('unauthenticated' in session) return UNAUTHENTICATED;
		const { ngoId } = session;
		// Files are already uploaded to S3 via presigned URLs — values are S3 keys
		const updatedProject = { ...project, ngoId };

		try {
			await createNgoProject(updatedProject);
		} catch (e) {
			const message = e instanceof Error ? e.message : '';
			if (message.includes('Item size has exceeded') || message.includes('ValidationException')) {
				return { error: 'PROJECT_TOO_LARGE' as const };
			}
			return { error: 'SAVE_PROJECT_FAILED' as const };
		}

		await refetch(ngoProjectsCacheOptions(ngoId));
		return { success: true as const };
	});

export const deleteNgoProjectFn = createServerFn({ method: 'POST' })
	.inputValidator(z.object({ projectId: z.string() }))
	.handler(async ({ data: { projectId } }) => {
		const session = requireSession();
		if ('unauthenticated' in session) return UNAUTHENTICATED;
		const { ngoId } = session;
		const result = await deleteNgoProject(projectId);
		await refetch(ngoProjectsCacheOptions(ngoId));
		return result;
	});

export const duplicateNgoProjectFn = createServerFn({ method: 'POST' })
	.inputValidator(z.object({ projectId: z.string(), name: translatableText, internalName: z.string() }))
	.handler(async ({ data: { projectId, name, internalName } }) => {
		const session = requireSession();
		if ('unauthenticated' in session) return UNAUTHENTICATED;
		const { ngoId } = session;
		const project = await getNgoProject(projectId);
		if ('notFound' in project) throw new Error('Project not found');

		const { id: _id, status: _status, creationDate: _creationDate, campaignIds: _campaignIds, ...rest } = project;
		const newId = crypto.randomUUID();

		await createNgoProject({
			...rest,
			id: newId,
			name,
			internalName: internalName || undefined,
			status: 'draft',
		});
		await refetch(ngoProjectsCacheOptions(ngoId));
		return { id: newId };
	});

async function transitionNgoProjectStatus(
	projectId: string,
	targetStatus: 'active' | 'paused',
	guard: (current: string) => string | null,
) {
	const session = requireSession();
	if ('unauthenticated' in session) return UNAUTHENTICATED;
	const { ngoId } = session;
	const project = await getNgoProject(projectId);
	if ('notFound' in project) throw new Error('Project not found');
	const error = guard(project.status);
	if (error) throw new Error(error);

	const { creationDate: _, ...attrs } = project;
	await createNgoProject({ ...attrs, status: targetStatus });
	await refetch(ngoProjectsCacheOptions(ngoId));
	return { success: true };
}

export const launchNgoProjectFn = createServerFn({ method: 'POST' })
	.inputValidator(z.object({ projectId: z.string() }))
	.handler(({ data: { projectId } }) =>
		transitionNgoProjectStatus(projectId, 'active', (s) => (s === 'active' ? 'Project is already active' : null)),
	);

export const pauseNgoProjectFn = createServerFn({ method: 'POST' })
	.inputValidator(z.object({ projectId: z.string() }))
	.handler(({ data: { projectId } }) =>
		transitionNgoProjectStatus(projectId, 'paused', (s) =>
			s !== 'active' ? 'Only active projects can be paused' : null,
		),
	);

export const unpauseNgoProjectFn = createServerFn({ method: 'POST' })
	.inputValidator(z.object({ projectId: z.string() }))
	.handler(({ data: { projectId } }) =>
		transitionNgoProjectStatus(projectId, 'active', (s) =>
			s !== 'paused' ? 'Only paused projects can be unpaused' : null,
		),
	);

export const getNgoPortalsFn = createServerFn().handler(() => {
	const session = requireSession();
	if ('unauthenticated' in session) return UNAUTHENTICATED;
	const { ngoId } = session;
	return getNgoPortals(ngoId);
});

export const getNgoPortalFn = createServerFn()
	.inputValidator(z.object({ portalId: z.string() }))
	.handler(({ data: { portalId } }) => {
		const session = requireSession();
		if ('unauthenticated' in session) return UNAUTHENTICATED;
		const { ngoId } = session;
		return getNgoPortal(ngoId, portalId);
	});

export const updateNgoPortalFn = createServerFn({ method: 'POST' })
	.inputValidator(
		z.object({
			portalId: z.string(),
			title: z.string().optional(),
			presentation: z.string().optional(),
			themeBaseColor: z.string().optional(),
			themeSecondaryColor: z.string().optional(),
			amountOptions: z.array(z.record(z.string(), z.number())).optional(),
			logoImage: z.string().optional(),
			coverImage: z.string().optional(),
			languages: z.array(z.enum(languages)).optional(),
			textsCollection: z
				.array(
					z.object({
						langCode: z.enum(languages),
						presentation: z.string().optional(),
						title: z.string().optional(),
						imageAlt: z.string().optional(),
						donateButton: z.string().optional(),
						faqItems: z.array(z.object({ question: z.string(), answer: z.string() })).optional(),
						amountEquivalents: z.array(z.string()).optional(),
					}),
				)
				.optional(),
			sliderItems: z
				.array(
					z.object({
						texts: z.array(
							z.object({
								langCode: z.enum(languages),
								quote: z.string(),
								authorName: z.string(),
								imageAlt: z.string().optional(),
							}),
						),
					}),
				)
				.optional(),
			sliderImages: z.array(z.object({ index: z.number(), image: z.string() })).optional(),
		}),
	)
	.handler(({ data: { portalId, ...input } }) => {
		const session = requireSession();
		if ('unauthenticated' in session) return UNAUTHENTICATED;
		const { ngoId } = session;
		return updateNgoPortal(ngoId, portalId, input);
	});

export const getNgoSettingsFn = createServerFn()
	.inputValidator(z.object({ portalId: z.string() }))
	.handler(async ({ data: { portalId } }) => {
		const session = requireSession();
		if ('unauthenticated' in session) return UNAUTHENTICATED;
		const { ngoId } = session;
		const result = await getNgoSettings(portalId);
		if ('notFound' in result) return result;
		// Verify the portal belongs to the authenticated NGO (admins can access any)
		if (!isAdminSession() && result.id !== ngoId) {
			throw new Error('Unauthorized: portal does not belong to this NGO');
		}
		return result;
	});

const ngoSettingsInputSchema = z.object({
	portalId: z.string(),
	name: z.string().optional(),
	country: z.string().optional(),
	city: z.string().optional(),
	region: z.string().optional(),
	province: z.string().optional(),
	postalCode: z.string().optional(),
	address: z.string().optional(),
	web: z.string().optional(),
	taxId: z.string().optional(),
	countries: z.array(z.string()).optional(),
	actionScope: z
		.array(
			z.enum([
				'humanRights',
				'education',
				'environment',
				'health',
				'waterAndEnergy',
				'foodSecurity',
				'habitatAndPoverty',
			]),
		)
		.optional(),
	contact1Firstname: z.string().optional(),
	contact1Lastname: z.string().optional(),
	contact1Position: z.string().optional(),
	contact1Email: z.string().optional(),
	contact1Phone: z.string().optional(),
	contact2Firstname: z.string().optional(),
	contact2Lastname: z.string().optional(),
	contact2Position: z.string().optional(),
	contact2Email: z.string().optional(),
	contact2Phone: z.string().optional(),
	contactLegalFirstname: z.string().optional(),
	contactLegalLastname: z.string().optional(),
	contactLegalPosition: z.string().optional(),
	contactLegalId: z.string().optional(),
	donationCertificates: z.boolean().optional(),
	certifiedByFoundation: z.boolean().optional(),
	certifiedByFoundationDescription: z.string().optional(),
	corporateVolunteering: z.boolean().optional(),
	corporateVolunteeringDescription: z.string().optional(),
	privacyUrl: z.string().optional(),
	privacyEmail: z.string().optional(),
	privacyCountry: z.string().optional(),
	privacyCity: z.string().optional(),
	privacyRegion: z.string().optional(),
	privacyProvince: z.string().optional(),
	privacyPostalCode: z.string().optional(),
	privacyAddress: z.string().optional(),
	privacyPhone: z.string().optional(),
	bankIBANCode: z.string().optional(),
	bankSwiftCode: z.string().optional(),
	bankAccountNumber: z.string().optional(),
	bankCurrency: z.enum(currencyCodes).optional(),
	bankAccountName: z.string().optional(),
	bankCountry: z.string().optional(),
	bankCity: z.string().optional(),
	bankRegion: z.string().optional(),
	bankProvince: z.string().optional(),
	bankPostalCode: z.string().optional(),
	bankAddress: z.string().optional(),
	language: z.enum(languages).optional(),
	hasSignedAgreement: z.boolean().optional(),
	version: z.number().optional(),
});

export const putNgoSettingsFn = createServerFn({ method: 'POST' })
	.inputValidator(ngoSettingsInputSchema)
	.handler(async ({ data }) => {
		const session = requireSession();
		if ('unauthenticated' in session) return UNAUTHENTICATED;
		const { ngoId } = session;
		// Verify portal ownership before allowing mutation (admins can access any)
		if (!isAdminSession()) {
			const existing = await getNgoSettings(data.portalId);
			if ('notFound' in existing || existing.id !== ngoId) {
				throw new Error('Unauthorized: portal does not belong to this NGO');
			}
		}
		const { portalId, ...input } = data;
		return putNgoSettings(portalId, input);
	});

export const getNgoProfileFn = createServerFn().handler(async () => {
	const session = requireSession();
	if ('unauthenticated' in session) return UNAUTHENTICATED;
	const { ngoId } = session;
	// Check draft NGOs first (created through onboarding)
	const [draft] = await getDraftNgos([ngoId]);

	if (draft) {
		const logoValue = typeof draft.logo === 'string' ? draft.logo : undefined;
		const certValue = typeof draft.bankAccountCertificate === 'string' ? draft.bankAccountCertificate : undefined;
		const [logoUrl, bankAccountCertificateUrl] = await Promise.all([
			resolveFileUrl(logoValue),
			resolveFileUrl(certValue),
		]);

		return {
			isDraft: true as const,
			id: draft.id,
			name: draft.name,
			logo: logoUrl,
			web: draft.web,
			taxId: draft.taxId,
			country: draft.country,
			countries: draft.countries,
			address: draft.address,
			postalCode: draft.postalCode,
			city: draft.city,
			region: draft.region,
			province: draft.province,
			actionScope: draft.actionScope,
			privacyUrl: draft.privacyUrl,
			privacyEmail: draft.privacyEmail,
			privacyPhone: draft.privacyPhone,
			privacyAddress: draft.privacyAddress,
			privacyCity: draft.privacyCity,
			privacyCountry: draft.privacyCountry,
			privacyPostalCode: draft.privacyPostalCode,
			privacyRegion: draft.privacyRegion,
			privacyProvince: draft.privacyProvince,
			contact1Firstname: draft.contact1Firstname,
			contact1Lastname: draft.contact1Lastname,
			contact1Position: draft.contact1Position,
			contact1Email: draft.contact1Email,
			contact1Phone: draft.contact1Phone,
			contact2Firstname: draft.contact2Firstname,
			contact2Lastname: draft.contact2Lastname,
			contact2Position: draft.contact2Position,
			contact2Email: draft.contact2Email,
			contact2Phone: draft.contact2Phone,
			contactLegalFirstname: draft.contactLegalFirstname,
			contactLegalLastname: draft.contactLegalLastname,
			contactLegalPosition: draft.contactLegalPosition,
			contactLegalId: draft.contactLegalId,
			donationCertificates: draft.donationCertificates,
			bankAccountName: draft.bankAccountName,
			bankCurrency: draft.bankCurrency,
			bankInfo: draft.bankInfo,
			bankCountry: draft.bankCountry,
			bankCity: draft.bankCity,
			bankRegion: draft.bankRegion,
			bankProvince: draft.bankProvince,
			bankPostalCode: draft.bankPostalCode,
			bankAddress: draft.bankAddress,
			language: draft.language,
			// These fields are added dynamically after onboarding and not in the base schema
			languages:
				'languages' in draft && Array.isArray(draft.languages)
					? draft.languages.filter((l): l is string => typeof l === 'string')
					: undefined,
			defaultLanguage:
				'defaultLanguage' in draft && typeof draft.defaultLanguage === 'string' ? draft.defaultLanguage : undefined,
			bankAccountCertificate: certValue,
			bankAccountCertificateUrl,
			certifiedByFoundation: draft.certifiedByFoundation,
			certifiedByFoundationDescription: draft.certifiedByFoundationDescription,
			corporateVolunteering: draft.corporateVolunteering,
			corporateVolunteeringDescription: draft.corporateVolunteeringDescription,
			allowedEmails:
				'allowedEmails' in draft && Array.isArray(draft.allowedEmails)
					? draft.allowedEmails.filter((e): e is string => typeof e === 'string')
					: [],
		};
	}

	// Fallback: check real NGOs (already created in the system)
	const ngo = await getNgoById(ngoId);
	if (!ngo) return { notFound: true } as const;

	const logoLocation = ngo.logo?.location;
	const certValue = ngo.bankAccountCertificate;
	const [logoUrl, bankAccountCertificateUrl] = await Promise.all([
		resolveFileUrl(logoLocation),
		resolveFileUrl(certValue),
	]);

	return {
		isDraft: false as const,
		id: ngo.id,
		name: ngo.name,
		logo: logoUrl,
		web: ngo.url,
		taxId: ngo.taxIdCode,
		country: ngo.countryCode,
		countries: ngo.countries,
		address: ngo.address,
		postalCode: ngo.postalCode,
		city: ngo.city,
		region: ngo.region?.toString(),
		province: ngo.province?.toString(),
		actionScope: ngo.actionScope,
		privacyUrl: ngo.dataProtectionResponsible?.url,
		privacyEmail: ngo.dataProtectionResponsible?.email,
		privacyPhone: ngo.dataProtectionResponsible?.phone,
		privacyAddress: ngo.dataProtectionResponsible?.address,
		privacyCity: ngo.dataProtectionResponsible?.city,
		privacyCountry: ngo.dataProtectionResponsible?.countryCode,
		privacyPostalCode: ngo.dataProtectionResponsible?.postalCode,
		privacyRegion: ngo.dataProtectionResponsible?.region?.toString(),
		privacyProvince: ngo.dataProtectionResponsible?.province?.toString(),
		contact1Firstname: ngo.contact?.name,
		contact1Lastname: ngo.contact?.surname,
		contact1Position: ngo.contact?.position,
		contact1Email: ngo.contact?.email,
		contact1Phone: ngo.contact?.phoneNumber,
		contact2Firstname: ngo.contact2?.name,
		contact2Lastname: ngo.contact2?.surname,
		contact2Position: ngo.contact2?.position,
		contact2Email: ngo.contact2?.email,
		contact2Phone: ngo.contact2?.phoneNumber,
		contactLegalFirstname: ngo.responsible?.name,
		contactLegalLastname: ngo.responsible?.surname,
		contactLegalPosition: ngo.responsible?.position,
		contactLegalId: ngo.responsible?.idDocument,
		donationCertificates: ngo.emitCertificates,
		bankAccountName: ngo.billing.beneficiary?.name,
		bankCurrency: ngo.billing.currencyCode,
		bankInfo: {
			wantSwiftAndCC: !!(ngo.billing.swift || ngo.billing.bankAccount),
			bankSwiftCode: ngo.billing.swift,
			bankAccountNumber: ngo.billing.bankAccount,
			bankIBANCode: ngo.billing.IBAN,
		},
		bankCountry: ngo.billing.beneficiary?.physicalLocation?.countryCode,
		bankCity: ngo.billing.beneficiary?.physicalLocation?.city,
		bankRegion: ngo.billing.beneficiary?.physicalLocation?.region?.toString(),
		bankProvince: ngo.billing.beneficiary?.physicalLocation?.province?.toString(),
		bankPostalCode: ngo.billing.beneficiary?.physicalLocation?.postalCode,
		bankAddress: ngo.billing.beneficiary?.physicalLocation?.address,
		language: ngo.contact.languageCode,
		languages: undefined,
		defaultLanguage: undefined,
		bankAccountCertificate: certValue,
		bankAccountCertificateUrl,
		certifiedByFoundation: ngo.certifiedByFoundation,
		certifiedByFoundationDescription: ngo.certifiedByFoundationDescription,
		corporateVolunteering: ngo.corporateVolunteering,
		corporateVolunteeringDescription: ngo.corporateVolunteeringDescription,
		allowedEmails: ngo.allowedEmails ?? [],
	};
});

const ngoProfileDataSchema = ngoSettingsInputSchema.omit({ portalId: true }).extend({
	logo: z.string().optional(),
	logoWidth: z.number().optional(),
	logoHeight: z.number().optional(),
	bankAccountCertificate: z.string().optional(),
	allowedEmails: z.array(z.string()).optional(),
	bankInfo: z
		.object({
			bankSwiftCode: z.string().optional(),
			bankAccountNumber: z.string().optional(),
			bankIBANCode: z.string().optional(),
		})
		.optional(),
});

export const putNgoProfileFn = createServerFn({ method: 'POST' })
	.inputValidator(z.object({ data: z.record(z.string(), z.unknown()) }))
	.handler(async ({ data: { data: rawData } }) => {
		const session = requireSession();
		if ('unauthenticated' in session) return UNAUTHENTICATED;
		const { ngoId } = session;
		const data = ngoProfileDataSchema.parse(rawData);
		const [draft] = await getDraftNgos([ngoId]);

		if (draft) {
			// Draft NGO path (created through onboarding, not yet activated)
			// Files are already uploaded to S3 via presigned URLs — values are S3 keys
			const updatedData: Record<string, unknown> = { ...data };
			for (const field of ['logo', 'bankAccountCertificate'] as const) {
				if (updatedData[field] === '') updatedData[field] = undefined;
			}

			const updated = { ...draft, ...updatedData, id: ngoId };
			await putDraftNgos(updated);
			return { success: true } as const;
		}

		// Real NGO path (already activated in the system)
		const { bankInfo, logoWidth, logoHeight, ...profileFields } = data;
		const result = await putNgoProfileById(ngoId, {
			...profileFields,
			logoWidth,
			logoHeight,
			bankIBANCode: bankInfo?.bankIBANCode,
			bankSwiftCode: bankInfo?.bankSwiftCode,
			bankAccountNumber: bankInfo?.bankAccountNumber,
		});
		if ('notFound' in result) return { notFound: true } as const;
		return { success: true } as const;
	});
